/**
 * On start, params are stored from GET parameters.
 * Application is also stored for display and verification information.
 */
import { FeatureFlagsMap } from 'inquiry/lib/interfaces/feature-flags';
import { ProductFeaturesMap } from 'inquiry/lib/ProductFeatures';
import { ClientSideCollectedData } from 'inquiry/lib/types';
import Action from 'lib/interfaces/Action';
import { Step } from 'lib/interfaces/StepsConfig';
import { UiComponent } from 'lib/interfaces/ui-components';
import { DocumentGeneric } from 'verify/models/document';
import { Inquiry } from 'verify/models/inquiry';
import { InquirySession } from 'verify/models/inquiry-session';
import { InquiryTemplateVersion } from 'verify/models/inquiry-template-version';
import { Organization } from 'verify/models/organization';
import { Template } from 'verify/models/template';
import { Theme } from 'verify/models/theme';
import { Verification } from 'verify/models/verification';

export enum InquiryActionType {
  LoadVerifications = 'inquiry/LOAD_VERIFICATIONS',
  LogCollectedData = 'inquiry/LOG_COLLECTED_DATA',
  SaveVerification = 'inquiry/SAVE_VERIFICATION',
  LoadDocuments = 'inquiry/LOAD_DOCUMENTS',
  UpdateDocumentAtIndex = 'inquiry/UPDATE_DOCUMENT_AT_INDEX',
  SaveTemplate = 'inquiry/SAVE_TEMPLATE',
  SaveInquiryTemplateVersion = 'inquiry/SAVE_INQUIRY_TEMPLATE_VERSION',
  SaveInquiry = 'inquiry/SAVE_INQUIRY',
  SaveOrganization = 'inquiry/SAVE_ORGANIZATION',
  SaveTheme = 'inquiry/SAVE_THEME',
  SaveIsWebGLIncompatible = 'inquiry/SAVE_WEB_GL_INCOMPATIBILITY',
  SavePopupUrl = 'inquiry/SAVE_POPUP_URL',
  SaveFeatureFlags = 'inquiry/SAVE_FEATURE_FLAGS',
  SaveProductFeatures = 'inquiry/SAVE_PRODUCT_FEATURES',
  SetCurrentSession = 'inquiry/SET_CURRENT_SESSION',
  SetCurrentStep = 'inquiry/SET_CURRENT_STEP',
  SetComponent = 'inquiry/SET_COMPONENT',
}

interface LoadVerificationsPayload {
  verifications: Verification[];
}

interface LoadDocumentsPayload {
  documents: DocumentGeneric[];
}
interface LogCollectedDataPayload {
  data: ClientSideCollectedData;
}

interface SaveVerificationPayload {
  verification: Verification;
}

interface UpdateDocumentAtIndexPayload {
  document: DocumentGeneric;
  documentNumber: number;
}

interface SaveTemplatePayload {
  template: Template;
}

interface SaveInquiryTemplateVersionPayload {
  inquiryTemplateVersion: InquiryTemplateVersion;
}

interface SaveInquiryPayload {
  inquiry: Inquiry;
}

interface SaveOrganizationPayload {
  organization: Organization;
}

interface SaveThemePayload {
  theme: Theme;
}

interface SaveIsWebGLIncompatiblePayload {
  isWebGLIncompatible: boolean;
}

interface SavePopupUrlPayload {
  popupUrl: string | null;
}

interface SaveFeatureFlagsPayload {
  featureFlags: FeatureFlagsMap;
}

interface SaveProductFeaturesPayload {
  productFeatures: ProductFeaturesMap;
}

interface SetCurrentSessionPayload {
  session: InquirySession;
}

interface SetCurrentStepPayload {
  step: Step;
}

interface SetComponentPayload {
  component: UiComponent;
}

export function loadVerifications(verifications: Verification[]) {
  return {
    payload: {
      verifications,
    },
    type: InquiryActionType.LoadVerifications,
  };
}

export function saveVerification(verification: Verification) {
  return {
    payload: {
      verification,
    },
    type: InquiryActionType.SaveVerification,
  };
}

export function loadDocuments(documents: DocumentGeneric[]) {
  return {
    payload: {
      documents,
    },
    type: InquiryActionType.LoadDocuments,
  };
}

export function logCollectedData(data: ClientSideCollectedData) {
  return {
    payload: {
      data,
    },
    type: InquiryActionType.LogCollectedData,
  };
}

export function updateDocumentAtIndex(document: DocumentGeneric, documentNumber: number) {
  return {
    payload: {
      document,
      documentNumber,
    },
    type: InquiryActionType.UpdateDocumentAtIndex,
  };
}

export function saveTemplate(template: Template) {
  return {
    payload: {
      template,
    },
    type: InquiryActionType.SaveTemplate,
  };
}

export function saveInquiryTemplateVersion(inquiryTemplateVersion: InquiryTemplateVersion) {
  return {
    payload: {
      inquiryTemplateVersion,
    },
    type: InquiryActionType.SaveInquiryTemplateVersion,
  };
}

export function saveInquiry(inquiry: Inquiry) {
  return {
    payload: {
      inquiry,
    },
    type: InquiryActionType.SaveInquiry,
  };
}

export function saveOrganization(organization: Organization) {
  return {
    payload: {
      organization,
    },
    type: InquiryActionType.SaveOrganization,
  };
}

export function saveTheme(theme: Theme) {
  return {
    payload: {
      theme,
    },
    type: InquiryActionType.SaveTheme,
  };
}

export function saveIsWebGLIncompatible(isWebGLIncompatible: boolean) {
  return {
    payload: {
      isWebGLIncompatible,
    },
    type: InquiryActionType.SaveIsWebGLIncompatible,
  };
}

export function savePopupUrl(popupUrl: string | null) {
  return {
    payload: {
      popupUrl,
    },
    type: InquiryActionType.SavePopupUrl,
  };
}

export function saveFeatureFlags(featureFlags: FeatureFlagsMap) {
  return {
    payload: {
      featureFlags,
    },
    type: InquiryActionType.SaveFeatureFlags,
  };
}

export function saveProductFeatures(productFeatures: ProductFeaturesMap) {
  return {
    payload: {
      productFeatures,
    },
    type: InquiryActionType.SaveProductFeatures,
  };
}

export function setCurrentSession(session: InquirySession) {
  return {
    payload: {
      session,
    },
    type: InquiryActionType.SetCurrentSession,
  };
}

/**
 * This is used to update the current step independently of the inquiry.
 * Most of the time, this is always called alongside saveInquiry().
 * Sometimes, we want to update the inquiry but not reroute the user
 * (e.g. for optional documents upload).
 */
export function setCurrentStep(step: Step) {
  return {
    payload: {
      step,
    },
    type: InquiryActionType.SetCurrentStep,
  };
}

export function setComponent(component: UiComponent) {
  return {
    payload: {
      component,
    },
    type: InquiryActionType.SetComponent,
  };
}

export type InquiryActions =
  | Action<InquiryActionType.LoadVerifications, LoadVerificationsPayload>
  | Action<InquiryActionType.LogCollectedData, LogCollectedDataPayload>
  | Action<InquiryActionType.SaveVerification, SaveVerificationPayload>
  | Action<InquiryActionType.LoadDocuments, LoadDocumentsPayload>
  | Action<InquiryActionType.UpdateDocumentAtIndex, UpdateDocumentAtIndexPayload>
  | Action<InquiryActionType.SaveTemplate, SaveTemplatePayload>
  | Action<InquiryActionType.SaveInquiryTemplateVersion, SaveInquiryTemplateVersionPayload>
  | Action<InquiryActionType.SaveInquiry, SaveInquiryPayload>
  | Action<InquiryActionType.SaveOrganization, SaveOrganizationPayload>
  | Action<InquiryActionType.SaveTheme, SaveThemePayload>
  | Action<InquiryActionType.SaveIsWebGLIncompatible, SaveIsWebGLIncompatiblePayload>
  | Action<InquiryActionType.SavePopupUrl, SavePopupUrlPayload>
  | Action<InquiryActionType.SaveFeatureFlags, SaveFeatureFlagsPayload>
  | Action<InquiryActionType.SaveProductFeatures, SaveProductFeaturesPayload>
  | Action<InquiryActionType.SetCurrentSession, SetCurrentSessionPayload>
  | Action<InquiryActionType.SetCurrentStep, SetCurrentStepPayload>
  | Action<InquiryActionType.SetComponent, SetComponentPayload>;
